import { FiPause, FiPlay, FiX } from 'react-icons/fi';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  EVENTS,
  PROPERTIES,
  trackEvent,
  TRACKING_SOURCES,
} from '@/services/posthog';
import { isVideoUrl } from '@/utils/mediaUtils';
import {
  REELS_PLAY_STATUS_INDICATOR_DURATION,
  REELS_SCROLL_SWITCH_COOLDOWN,
  REELS_SWIPE_THRESHOLD,
  REELS_WHEEL_SCROLL_THRESHOLD,
} from './ReelsPlayerOverlay.constants';
import styles from './ReelsPlayerOverlay.module.css';
import type { ReelsPlayerOverlayProps } from './ReelsPlayerOverlay.types';

export const ReelsPlayerOverlay: React.FC<ReelsPlayerOverlayProps> = ({
  videos,
  initialIndex,
  onClose,
  productId,
}) => {
  const [currentIndex, setCurrentIndex] = useState<number>(initialIndex);
  const [isPlaying, setIsPlaying] = useState<boolean>(true);
  const [showPlayStateIndicator, setShowPlayStateIndicator] =
    useState<boolean>(false);

  const videoRef = useRef<HTMLVideoElement>(null);
  const lastScrollTime = useRef<number>(0);
  const touchStartY = useRef<number>(0);
  const touchEndY = useRef<number>(0);
  const startTimeRef = useRef<number>(0);
  const activeVideo = videos[currentIndex];
  const activeIsVideo = activeVideo ? isVideoUrl(activeVideo.videoUrl) : true;

  const handlePrev = useCallback((): void => {
    if (currentIndex > 0) {
      setCurrentIndex((prev) => prev - 1);
      setIsPlaying(true);
    }
  }, [currentIndex]);

  const handleNext = useCallback((): void => {
    if (currentIndex < videos.length - 1) {
      setCurrentIndex((prev) => prev + 1);
      setIsPlaying(true);
    }
  }, [currentIndex, videos.length]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !activeIsVideo) return;

    if (isPlaying) {
      video.play().catch((err) => {
        console.warn('Playback failed:', err);
      });
    } else {
      video.pause();
    }
  }, [isPlaying, currentIndex, activeIsVideo]);

  useEffect(() => {
    startTimeRef.current = Date.now();
    const currentVideo = videoRef.current;

    trackEvent(EVENTS.PDP_V2_REEL_OPEN, {
      [PROPERTIES.PRODUCT_ID]: productId || 0,
      [PROPERTIES.VIDEO_ID]: activeVideo?.id || '',
      [PROPERTIES.VIDEO_INDEX]: currentIndex,
      [PROPERTIES.SOURCE]: TRACKING_SOURCES.PDP_V2,
    });

    return () => {
      const viewDurationMs = Date.now() - startTimeRef.current;
      const viewDurationSec = Math.round(viewDurationMs / 1000);

      const completed =
        currentVideo && currentVideo.duration
          ? currentVideo.currentTime / currentVideo.duration > 0.9
          : viewDurationSec > 15;

      trackEvent(EVENTS.PDP_V2_REEL_VIEWED, {
        [PROPERTIES.PRODUCT_ID]: productId || 0,
        [PROPERTIES.VIDEO_ID]: activeVideo?.id || '',
        [PROPERTIES.VIDEO_INDEX]: currentIndex,
        [PROPERTIES.VIEW_DURATION_SECONDS]: viewDurationSec,
        [PROPERTIES.COMPLETED]: completed,
        [PROPERTIES.SOURCE]: TRACKING_SOURCES.PDP_V2,
      });
    };
  }, [currentIndex, activeVideo, productId]);

  const togglePlayPause = useCallback((): void => {
    setIsPlaying((prev) => {
      const nextState = !prev;
      trackEvent(EVENTS.PDP_V2_REEL_PLAYBACK_TOGGLE, {
        [PROPERTIES.PRODUCT_ID]: productId || 0,
        [PROPERTIES.VIDEO_ID]: activeVideo?.id || '',
        [PROPERTIES.IS_PLAYING]: nextState,
        [PROPERTIES.SOURCE]: TRACKING_SOURCES.PDP_V2,
      });
      return nextState;
    });
    setShowPlayStateIndicator(true);
    setTimeout(
      () => setShowPlayStateIndicator(false),
      REELS_PLAY_STATUS_INDICATOR_DURATION
    );
  }, [productId, activeVideo]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        handlePrev();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        handleNext();
      } else if (e.key === 'Escape') {
        onClose();
      } else if (e.key === ' ') {
        e.preventDefault();
        togglePlayPause();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handlePrev, handleNext, onClose, togglePlayPause]);

  const handleWheel = useCallback(
    (e: React.WheelEvent): void => {
      const now = Date.now();
      if (now - lastScrollTime.current < REELS_SCROLL_SWITCH_COOLDOWN) return;

      if (e.deltaY > REELS_WHEEL_SCROLL_THRESHOLD) {
        handleNext();
        lastScrollTime.current = now;
      } else if (e.deltaY < -REELS_WHEEL_SCROLL_THRESHOLD) {
        handlePrev();
        lastScrollTime.current = now;
      }
    },
    [handleNext, handlePrev]
  );

  const handleTouchStart = useCallback((e: React.TouchEvent): void => {
    touchStartY.current = e.targetTouches[0].clientY;
    touchEndY.current = e.targetTouches[0].clientY;
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent): void => {
    touchEndY.current = e.targetTouches[0].clientY;
  }, []);

  const handleTouchEnd = useCallback((): void => {
    const diffY = touchStartY.current - touchEndY.current;

    if (diffY > REELS_SWIPE_THRESHOLD) {
      handleNext();
    } else if (diffY < -REELS_SWIPE_THRESHOLD) {
      handlePrev();
    }
  }, [handleNext, handlePrev]);

  if (!activeVideo) return null;

  return (
    <div
      className={styles.overlay}
      onClick={onClose}
      onWheel={handleWheel}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <div
        className={styles.playerContainer}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.topHeader} onClick={(e) => e.stopPropagation()}>
          <button
            aria-label="Close reels player"
            className={styles.closeBtn}
            onClick={onClose}
          >
            <FiX size={24} />
          </button>
        </div>

        <div
          className={styles.videoWrapper}
          onClick={activeIsVideo ? togglePlayPause : undefined}
        >
          {activeIsVideo ? (
            <video
              autoPlay
              loop
              playsInline
              className={styles.videoElement}
              ref={videoRef}
              src={activeVideo.videoUrl}
            />
          ) : (
            <img
              className={styles.imageElement}
              src={activeVideo.videoUrl}
              alt={activeVideo.title || 'Media'}
            />
          )}
          {activeIsVideo && showPlayStateIndicator && (
            <div className={styles.playStateIndicator}>
              {isPlaying ? <FiPlay size={48} /> : <FiPause size={48} />}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReelsPlayerOverlay;
